/**
 * useMaintenanceStatus — delad hook för underhållsfönstrets läge.
 *
 * Backend äger all tidslogik; hooken hämtar bara resultatet. Ingen klocklogik
 * i frontend — annars skulle användarens systemtid kunna avgöra vad som visas,
 * och fönstret är definierat i svensk tid på servern.
 *
 * Används av bannern OCH av knappar som startar geokodning (Ny Zon, Geokod).
 * VIKTIGT: att gråa ut en knapp är UX, inte skydd. Backend avvisar fortfarande
 * anropet med 503 — en användare som redan hade sidan öppen när fönstret
 * började, eller som anropar API:et direkt, stoppas där. Ta aldrig bort
 * serverkontrollen med hänvisning till att knappen är inaktiverad.
 */
import { useCallback, useEffect, useState } from "react";
import Request from "../../utils/request";

// Fönstren är timmar långa, så en minut räcker och håller anropsvolymen låg.
const POLL_MS = 60_000;

const useMaintenanceStatus = () => {
  const [status, setStatus] = useState(null);

  /**
   * GF-50: the two callers of this function are NOT equivalent, and flagging
   * the function instead of the call sites re-opened the very hole GF-50 closes.
   *
   *  - on mount        ATTENDED. The user just loaded a page and is watching it
   *                    fill in. A dead token must log them out here.
   *  - on a timer tick UNATTENDED. Nobody is waiting; a logout would reload the
   *                    page out from under whatever they are typing.
   *
   * Why this matters more than it looks: /maintenance/status is the ONLY API
   * call made by two protected routes — Dashboard (route "/", which makes no
   * REST call of its own) and Settings for non-admins. "/" is also where a
   * successful login lands. Marking the mount call unattended therefore left a
   * dead session completely undetected on exactly those pages, with the app
   * looking logged in — GF-50's original symptom, reintroduced by its own fix.
   * Found by the targeted review pass, 2026-09-15.
   *
   * This buys detection at PAGE LOAD, not continuously. The banner lives in
   * Layout/Main.jsx outside {children}, so it does not remount on SPA
   * navigation: a token that dies while the user moves between / and /settings
   * still goes unnoticed until some call actually reaches the API. That is the
   * intended design (see request.js — "the logout happens on their next real
   * action"), but do not read the paragraph above as a promise of continuous
   * detection. Review pass 3, 2026-09-15.
   *
   * The default is attended, and the parameter is an options OBJECT rather than
   * a bare boolean, so that every way of getting it wrong fails loudly. Passing
   * the function straight to setInterval or to an onClick hands it no argument
   * or a React event; neither carries `unattended`, so both land on attended.
   * That is the same trap that made GF-65 survive three years — a handler used
   * as a direct reference receiving something truthy — pointed the safe way.
   */
  const fetchStatus = useCallback(async ({ unattended = false } = {}) => {
    const { response, error } = await Request("/maintenance/status", {
      method: "GET",
      unattended,
    });
    // Tyst fel: en trasig statusförfrågan ska aldrig störa den som arbetar.
    // Nästa poll försöker igen. Notera att status då förblir null, vilket
    // betyder att knappar INTE gråas ut — vi låser hellre upp för mycket i
    // frontend än blockerar fel, eftersom backend ändå har sista ordet.
    //
    // The silence holds for a 401 on the TIMER path only, and only because of
    // the unattended flag. On mount a 401 deliberately logs the user out.
    if (error || !response) return;
    setStatus(response);
  }, []);

  useEffect(() => {
    fetchStatus();
    // The arrow wrapper is load-bearing: setInterval(fetchStatus, POLL_MS) would
    // call it with no argument, which means attended, which means a timer tick
    // could hard-reload the page. Keep the wrapper.
    const timer = setInterval(() => fetchStatus({ unattended: true }), POLL_MS);
    return () => clearInterval(timer);
  }, [fetchStatus]);

  return {
    status,
    bannerActive: !!status?.bannerActive,
    blockActive: !!status?.blockActive,
    /** Färdig förklaring till tooltip/felmeddelande. Null när inget blockerar. */
    blockReason: status?.blockActive
      ? `${status.message || "Planerat underhåll."} Nya importer är pausade till ${status.blockEndLocal}.`
      : null,
  };
};

export default useMaintenanceStatus;
